'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { PlayIcon } from '@hugeicons/core-free-icons';
import NextImage from '@/features/Image';
import axios from '@/lib/axios';

interface Playlist {
  id: string;
  title: string;
  banner: string;
  video_count: number;
  orientation: string;
  is_popular: number;
  created_at: string;
  slug: string;
}

interface Filter {
  label: string;
  params: {
    order_by?: string;
    is_popular?: string;
  };
}

export default function ZepidtubePlaylists({
  initialPlaylists = [],
}: {
  initialPlaylists?: Playlist[];
}) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [playlistsData, setPlaylistsData] =
    useState<Playlist[]>(initialPlaylists);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filters: Filter[] = [
    { label: 'Latest', params: { order_by: 'latest' } },
    { label: 'Popular', params: { is_popular: '1' } },
    { label: 'Oldest', params: { order_by: 'oldest' } },
  ];
  const fetchPlaylists = async (params: Record<string, string>) => {
    try {
      setLoading(true);
      setError(null);
      const queryString = new URLSearchParams(params).toString();
      const response = await axios.get(
        `/api/v2/youtube-videos?orientation=landscape&${queryString}`
      );
      setPlaylistsData(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch playlists');
      console.error('Error fetching playlists:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterPlaylists = (index: number) => {
    setActiveIndex(index);
    const selectedFilter = filters[index];
    fetchPlaylists(selectedFilter.params);
  };

  // Load initial data if not provided
  useEffect(() => {
    if (initialPlaylists.length === 0) {
      fetchPlaylists({ order_by: 'latest' });
    }
  }, []);

  return (
    <div className="container">
      <div className="videos padding-top padding-bottom">
        <div className="section-header mb-4">
          <h5 className="pt-0 pb-0 mb-0">Multiple playlists</h5>
          <div className="section-header__actions">
            {filters.map((filter, index) => (
              <button
                key={index}
                onClick={() => filterPlaylists(index)}
                disabled={loading}
                className={`btn btn-sm text-dark btn-outline ml-2 ${activeIndex === index ? 'active' : ''}`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4">Loading playlists...</div>
        ) : error ? (
          <div className="text-center py-4 text-danger">{error}</div>
        ) : playlistsData?.length > 0 ? (
          <div className="row g-4 justify-content-center">
            {playlistsData.map((playlist) => (
              <div className="col-md-6 col-lg-3" key={playlist.id}>
                <article className="post">
                  <figure className="post__media post__video">
                    <Link href={`/zepid-tube/playlist-details/${playlist.id}`}>
                      <NextImage
                        src={playlist?.banner}
                        alt={playlist?.title}
                        width={1280}
                        height={500}
                        className="fade-in fadeIn"
                        style={{
                          animationDuration: '0.3s',
                          animationDelay: '0s',
                        }}
                      />
                    </Link>
                    <Link
                      href={`/zepid-tube/playlist-details/${playlist.id}`}
                      className="playlist__btn btn__iframe"
                    >
                      <span>
                        <HugeiconsIcon icon={PlayIcon} size={26} />
                      </span>
                      {playlist.video_count} Videos
                    </Link>
                  </figure>
                  <div className="post__body">
                    <h5 className="post__title fw-semibold">
                      <Link
                        href={`/zepid-tube/playlist-details/${playlist.id}`}
                        className="btn__video btn__iframe"
                      >
                        {playlist.title}
                      </Link>
                    </h5>
                    <div className="post__footer">
                      <Link
                        href={`/zepid-tube/playlist-details/${playlist.id}`}
                        className="post__footer-info"
                      >
                        View full playlist
                      </Link>
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
        ) : (
          <div className="row g-4 justify-content-center">
            <div className="col-md-6 col-lg-3">
              <h5 className="post__title fw-semibold">No playlists found</h5>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
